import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Calendar, Mail } from "lucide-react";
import { format } from "date-fns";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/tracking";
import { eventSrcSet } from "@/lib/responsiveImage";

/** Copy and figures below come from the private-events deck. Guest counts are
 *  the deck's (40/50/75/150), confirmed with the owner Sep 2026 — they replace
 *  the older 20/40/80 tiers that were on this page. */

const SPACE_STATS: [string, string][] = [
  ["14,000", "Square feet of private, climate-controlled club"],
  ["4", "Premium cushioned courts with non-glare lighting"],
  ["6+", "Lounge areas seating more than 100 guests"],
  ["150", "Guests at full buyout, with the run of the facility"],
  ["10 min", "From FiDi, with shuttle service and Uber codes available"],
];

const ADVANTAGES: [string, string][] = [
  ["Engaging activities beyond the court", "Unique games, ping pong, photo booths and team building exercises to keep everyone occupied between matches."],
  ["Technology built into the experience", "Live scoring on overhead screens, instant replay, and automated line calling on every court."],
  ["Full hot buffet catering", "Chef-prepared proteins and sides served on site — not a platter of sandwiches."],
  ["Activation carts", "Matcha, caviar, fresh juice and dessert carts wheeled straight onto the court."],
];

const AMENITIES: [string, string][] = [
  ["Photo booth", "Unlimited prints, branded strips available"],
  ["Stretch area", "Warm up and cool down off court"],
  ["Six-plus lounge zones", "Sectionals, dining table, coworking seats"],
  ["Private phone booths", "Soundproofed, for the call nobody can miss"],
  ["Replay system", "Every rally on the overhead screens"],
  ["Live line calling", "Real-time in/out calls — the only system of its kind in the world"],
];

const ON_COURT = [
  { img: "tournaments", alt: "Tournament group photo with trophies on court at Velto", title: "Tournaments & Leagues", copy: "Structured brackets, refereed matches, trophy ceremony." },
  { img: "coaching", alt: "A Velto coach guiding a first-time player at the net", title: "Coach-Guided Play", copy: "Never held a paddle? You'll be rallying within ten minutes." },
  { img: "team-games", alt: "A player mid-rally during a team building game", title: "Team Building Games", copy: "On-court games that keep everyone engaged no matter the skill level." },
  { img: "activations", alt: "A branded activation set up alongside the court", title: "Brand Activations", copy: "Bring a new audience to your brand — whatever you imagine, we build it." },
];

const OFF_COURT: [string, string][] = [
  ["Hot buffet", "chef-prepared proteins, sides and salads"],
  ["Matcha cart", "ceremonial grade, whisked to order"],
  ["Fresh juice bar", "three juices squeezed on site"],
  ["Caviar cart", "bumps and blinis with crème fraîche"],
  ["Balloons & decor", "brand-matched, set before doors open"],
];

const SOCIAL_SHOTS = [
  { img: "social-1", alt: "Trevor Noah and friends at Velto Pickleball Club" },
  { img: "social-2", alt: "A Liquid Death brand activation on site at Velto" },
  { img: "social-3", alt: "Pro player Anna Leigh Waters visiting the club" },
  { img: "social-4", alt: "Guests in the lounge during a private event" },
];

/** No prices on this page by design — pricing is quoted on enquiry. */
const PACKAGES = [
  { name: "Signature", guests: "Up to 40", duration: "2 hours", courts: "2 courts · 1 coach", lounge: "2 sections", arcade: "—", fb: "—", highlight: false },
  { name: "Premium", guests: "Up to 50", duration: "2 hours", courts: "4 courts · 2 coaches", lounge: "Full facility", arcade: "—", fb: "—", highlight: false },
  { name: "Velto Special", guests: "Up to 75", duration: "2 hours", courts: "4 courts · 2 coaches", lounge: "Full facility", arcade: "Unlimited", fb: "Included", highlight: true },
  { name: "Velto All-In", guests: "Up to 150", duration: "4 hours", courts: "4 courts · 4 coaches", lounge: "Full facility", arcade: "Unlimited", fb: "Included", highlight: false },
] as const;

const PACKAGE_ROWS: [string, keyof (typeof PACKAGES)[number]][] = [
  ["Guests", "guests"],
  ["Duration", "duration"],
  ["Courts & coaches", "courts"],
  ["Lounge", "lounge"],
  ["Arcade tech", "arcade"],
  ["Food & beverage", "fb"],
];

const DECOR_PACKAGES = [
  { name: "Classic", detail: "7 balloon bouquets · 1 oversized logo balloon", popular: false },
  { name: "Signature", detail: "Step & repeat backdrop with garland · 5 bouquets · logo balloon", popular: true },
  { name: "Luxury", detail: "Backdrop & garland · 2 balloon columns · 7 bouquets · logo balloon", popular: false },
];

const FB_CARTS = [
  { name: "Caviar Cart", detail: "Bumps and blinis with crème fraîche" },
  { name: "Kombucha Flight", detail: "Tasting flight, poured on court" },
  { name: "Fresh Juice Cart", detail: "Three juices squeezed on site" },
  { name: "Dessert Cart", detail: "Rotating pastry and sweets selection" },
  { name: "Velto Luxe Package", detail: "All four carts · full curated experience" },
];

const EYEBROW = "font-sweet-bold font-bold uppercase tracking-[0.18em] text-[11px] mb-3";
const H2 = "font-instrument text-3xl md:text-4xl leading-[1.1]";
const CTA =
  "inline-flex items-center justify-center bg-[hsl(var(--velto-copper))] hover:bg-[hsl(var(--velto-copper))]/90 text-[hsl(var(--velto-beige))] font-sweet-bold font-bold uppercase tracking-wider text-xs px-7 py-3.5 transition-transform hover:-translate-y-0.5 motion-reduce:transform-none";

/** One <img> treatment for the whole page: responsive srcset where the file is
 *  large enough to have variants, lazy below the fold, and a slow zoom on hover
 *  from the wrapping `group`. */
const EventImage = ({
  name,
  alt,
  sizes,
  priority = false,
  imgClass,
}: {
  name: string;
  alt: string;
  sizes: string;
  priority?: boolean;
  /** Extra utilities for the <img> — an off-centre crop, or taking it out
   *  of flow so its intrinsic height stops sizing the frame around it. */
  imgClass?: string;
}) => {
  const src = `/events/${name}.webp`;
  return (
    <img
      src={src}
      srcSet={eventSrcSet(src)}
      sizes={sizes}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : undefined}
      decoding="async"
      className={cn(
        "h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.045] motion-reduce:transition-none motion-reduce:group-hover:scale-100",
        imgClass,
      )}
    />
  );
};

const Events = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    eventType: "",
    estimatedGuests: "",
    preferredDate: undefined as Date | undefined,
    preferredTime: "",
    duration: "",
    foodBeverageInterest: "",
    budget: "",
    additionalInfo: ""
  });

  const eventTypes = [
    "Corporate Event",
    "Holiday Party",
    "Birthday Party",
    "Bachelor/Bachelorette Party",
    "Team Building",
    "Tournament",
    "Private Lessons Group",
    "Wedding/Engagement Party",
    "Fundraiser",
    "Other"
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim() || formData.firstName.trim().length < 2) {
      toast({ title: "Error", description: "Please enter a valid first name.", variant: "destructive" });
      return false;
    }
    if (!formData.lastName.trim() || formData.lastName.trim().length < 2) {
      toast({ title: "Error", description: "Please enter a valid last name.", variant: "destructive" });
      return false;
    }
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      toast({ title: "Error", description: "Please enter a valid email address.", variant: "destructive" });
      return false;
    }
    if (!formData.eventType) {
      toast({ title: "Error", description: "Please select an event type.", variant: "destructive" });
      return false;
    }
    if (!formData.estimatedGuests.trim()) {
      toast({ title: "Error", description: "Please enter estimated number of attendees.", variant: "destructive" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const { error } = await supabase.from("event_inquiries" as any).insert({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || null,
        event_type: formData.eventType,
        estimated_guests: formData.estimatedGuests.trim(),
        preferred_date: formData.preferredDate ? format(formData.preferredDate, "yyyy-MM-dd") : null,
        preferred_time: formData.preferredTime.trim() || null,
        duration: formData.duration.trim() || null,
        food_beverage_interest: formData.foodBeverageInterest === "yes" ? true : formData.foodBeverageInterest === "no" ? false : null,
        budget: formData.budget.trim() || null,
        additional_info: formData.additionalInfo.trim() || null
      });

      if (error) {
        if (error.message.includes("rate_limit") || error.code === "42501") {
          toast({ title: "Too Many Requests", description: "Please wait a few minutes before submitting another inquiry.", variant: "destructive" });
        } else {
          throw error;
        }
      } else {
        trackEvent("event_inquiry_submitted", {
          form_location: "events_page",
          event_type: formData.eventType || null,
          estimated_guests: formData.estimatedGuests || null,
        });
        navigate("/events-thankyou");
      }
    } catch (error) {
      console.error("Error submitting event inquiry:", error);
      toast({ title: "Error", description: "Something went wrong. Please try again later.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('inquiry-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--velto-beige))]">
      <Helmet>
        <title>Private &amp; Holiday Events | Velto Pickleball Club, Brooklyn NYC</title>
        <meta name="description" content="Host your next private event or holiday party at Velto Pickleball Club in Brooklyn. Corporate events, holiday parties, birthday parties, bachelor/bachelorette parties, team building & tournaments on 4 indoor courts." />
        <link rel="canonical" href="https://veltopb.com/events" />
        <meta property="og:title" content="Private & Holiday Events | Velto Pickleball Club, Brooklyn NYC" />
        <meta property="og:description" content="Host corporate events, holiday parties, birthday parties, and team building at Velto Pickleball Club on 4 indoor courts in Red Hook, Brooklyn." />
        <meta property="og:url" content="https://veltopb.com/events" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veltopb.com/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Private & Holiday Events | Velto Pickleball Club, Brooklyn NYC" />
        <meta name="twitter:description" content="Host corporate events, holiday parties, birthday parties, and team building at Velto Pickleball Club on 4 indoor courts in Red Hook, Brooklyn." />
        <meta name="twitter:image" content="https://veltopb.com/og-default.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://veltopb.com/" },
            { "@type": "ListItem", "position": 2, "name": "Private Events", "item": "https://veltopb.com/events" }
          ]
        })}</script>
        {/* Service schema for the event-hosting offering. NOT schema.org/Event —
            that requires a specific startDate, and Velto's events are on-demand
            private bookings, not scheduled dated events (a date would be invented). */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Private Pickleball Event Hosting",
          serviceType: "Private event venue",
          description:
            "Corporate events, holiday parties, birthday parties, bachelor/bachelorette parties, team building, and private group pickleball events at Velto Pickleball Club on four indoor courts in Red Hook, Brooklyn.",
          url: "https://veltopb.com/events",
          areaServed: [
            { "@type": "City", name: "Brooklyn" },
            { "@type": "City", name: "New York City" },
          ],
          provider: {
            "@type": "LocalBusiness",
            name: "Velto Pickleball Club",
            url: "https://veltopb.com",
            telephone: "+1-516-968-5204",
            email: "events@veltopb.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "160 Van Brunt Street",
              addressLocality: "Brooklyn",
              addressRegion: "NY",
              postalCode: "11231",
              addressCountry: "US",
            },
          },
        })}</script>
      </Helmet>
      <Header />

      <main className="pt-banner-5">
        {/* ---------------- INQUIRY FORM ----------------
            First block on the page, not the last: the form is the point of
            /events, so it opens rather than closes. id="inquiry-form" is still
            the scroll target for every CTA below, and the form itself, its
            validation and its Supabase insert are unchanged. The heading spans
            both columns so the form stays a direct grid child beside the
            photo — one nesting level, no re-indent of the 100 field lines. */}
        <section id="inquiry-form" className="pt-10 pb-14 md:pt-14 md:pb-20 bg-[hsl(var(--velto-forest))] text-[hsl(var(--velto-beige))]">
          {/* Stretch, not items-start: the photo column matches the form's
              height so the two end on the same line. */}
          <div className="container mx-auto px-4 md:px-6 grid gap-x-14 gap-y-9 lg:grid-cols-[1.05fr_1fr]">
            <div className="lg:col-span-2 max-w-[54ch]">
              <p className={`${EYEBROW} text-[hsl(var(--velto-copper-light))]`}>Private, Corporate &amp; Holiday Events</p>
              <h1 className="font-instrument text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
                Host your event or holiday party at Velto
              </h1>
              <p className="font-sweet mt-4 text-[hsl(var(--velto-beige))]/78 leading-relaxed">
                You bring the team, we'll handle every detail. Tell us roughly what you have in
                mind and we'll come back with availability and a quote. Events typically book four
                to six weeks out. Planning a holiday party? Send us your date and group size below.
              </p>
              <p className="font-sweet mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[hsl(var(--velto-beige))]/70">
                <Mail size={16} />
                <span>Or email us at</span>
                <a href="mailto:events@veltopb.com" className="text-[hsl(var(--velto-copper-light))] hover:underline">
                  events@veltopb.com
                </a>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 bg-[hsl(var(--velto-beige))]/10 backdrop-blur-sm p-6 rounded-lg border border-[hsl(var(--velto-beige))]/20">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName" className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">First Name *</Label>
                  <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="John" className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] placeholder:text-[hsl(var(--velto-beige))]/40 font-sweet h-9" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName" className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Last Name *</Label>
                  <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Doe" className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] placeholder:text-[hsl(var(--velto-beige))]/40 font-sweet h-9" required />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Email *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="john@example.com" className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] placeholder:text-[hsl(var(--velto-beige))]/40 font-sweet h-9" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Phone</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="(555) 123-4567" className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] placeholder:text-[hsl(var(--velto-beige))]/40 font-sweet h-9" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Event Type *</Label>
                  <Select value={formData.eventType} onValueChange={(v) => handleSelectChange("eventType", v)}>
                    <SelectTrigger className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] font-sweet h-9">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((type) => (
                        <SelectItem key={type} value={type} className="font-sweet">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="estimatedGuests" className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Est. Attendees *</Label>
                  <Input id="estimatedGuests" name="estimatedGuests" value={formData.estimatedGuests} onChange={handleInputChange} placeholder="e.g., 25" className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] placeholder:text-[hsl(var(--velto-beige))]/40 font-sweet h-9" required />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Preferred Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-sweet h-9 bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] hover:bg-white/20 hover:text-[hsl(var(--velto-beige))]", !formData.preferredDate && "text-[hsl(var(--velto-beige))]/40")}>
                        <Calendar className="mr-2 h-4 w-4" />
                        {formData.preferredDate ? format(formData.preferredDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent mode="single" selected={formData.preferredDate} onSelect={(date) => setFormData(prev => ({ ...prev, preferredDate: date }))} disabled={(date) => date < new Date()} initialFocus className="p-3 pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="preferredTime" className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Preferred Time</Label>
                  <Input id="preferredTime" name="preferredTime" value={formData.preferredTime} onChange={handleInputChange} placeholder="e.g., 2:00 PM" className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] placeholder:text-[hsl(var(--velto-beige))]/40 font-sweet h-9" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="duration" className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Duration</Label>
                  <Input id="duration" name="duration" value={formData.duration} onChange={handleInputChange} placeholder="e.g., 3 hours" className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] placeholder:text-[hsl(var(--velto-beige))]/40 font-sweet h-9" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="budget" className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Budget</Label>
                  <Input id="budget" name="budget" value={formData.budget} onChange={handleInputChange} placeholder="e.g., $1,000 - $2,000" className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] placeholder:text-[hsl(var(--velto-beige))]/40 font-sweet h-9" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Food & Beverage Interest?</Label>
                <RadioGroup value={formData.foodBeverageInterest} onValueChange={(v) => handleSelectChange("foodBeverageInterest", v)} className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="fb-yes" className="border-[hsl(var(--velto-beige))] text-[hsl(var(--velto-copper))]" />
                    <Label htmlFor="fb-yes" className="font-sweet text-[hsl(var(--velto-beige))]">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="fb-no" className="border-[hsl(var(--velto-beige))] text-[hsl(var(--velto-copper))]" />
                    <Label htmlFor="fb-no" className="font-sweet text-[hsl(var(--velto-beige))]">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="additionalInfo" className="text-[hsl(var(--velto-beige))] font-sweet-bold text-xs uppercase tracking-wider">Additional Info</Label>
                <Textarea id="additionalInfo" name="additionalInfo" value={formData.additionalInfo} onChange={handleInputChange} placeholder="Tell us more about your event..." rows={3} className="bg-white/10 border-[hsl(var(--velto-beige))]/20 text-[hsl(var(--velto-beige))] placeholder:text-[hsl(var(--velto-beige))]/40 font-sweet resize-none" />
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full bg-[hsl(var(--velto-copper))] hover:bg-[hsl(var(--velto-copper))]/90 text-[hsl(var(--velto-beige))] font-sweet-bold uppercase tracking-wider py-5">
                {isSubmitting ? "Submitting..." : "Submit Inquiry"}
              </Button>
            </form>

            <div className="flex flex-col">
              {/* From lg up the photo takes whatever height the contact list
                  leaves, so the column ends level with the form. No fixed
                  ratio would do it — the leftover is ~486px at every width,
                  which is landscape at 1440 and portrait by 1024.

                  The image goes absolute at lg for a reason: a flex item's
                  intrinsic height still sizes an auto-height container even
                  at flex-basis 0, so left in flow the 3:4 photo sizes this
                  column and the *form* stretches to it — the inverse of
                  what we want. Out of flow it contributes nothing, the
                  column takes the form's height, and flex-1 hands the photo
                  the remainder.

                  That crop is anchored to the top: centred, the widest
                  frames cut the dog's head. Mobile has nothing to line up
                  with, so it keeps a 4/5 frame (~3% off a 3:4 original). */}
              <div className="group relative aspect-[4/5] lg:aspect-auto lg:flex-1 lg:min-h-0 overflow-hidden rounded-lg bg-[hsl(var(--velto-beige))]/5">
                <EventImage
                  name="paddle-dog"
                  alt="A club member holding their dog and a Velto paddle courtside"
                  sizes="(min-width:1024px) 44vw, 92vw"
                  imgClass="object-top lg:absolute lg:inset-0"
                  priority
                />
              </div>
              <dl className="mt-8 pt-6 border-t border-[hsl(var(--velto-beige))]/15 grid gap-5 sm:grid-cols-2">
                <div>
                  <dt className="font-sweet-bold font-bold uppercase tracking-[0.17em] text-[10px] text-[hsl(var(--velto-copper-light))] mb-1.5">
                    Email
                  </dt>
                  <dd className="font-instrument text-xl">
                    <a href="mailto:events@veltopb.com" className="underline underline-offset-4 decoration-[hsl(var(--velto-beige))]/30 hover:decoration-[hsl(var(--velto-beige))]">
                      events@veltopb.com
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="font-sweet-bold font-bold uppercase tracking-[0.17em] text-[10px] text-[hsl(var(--velto-copper-light))] mb-1.5">
                    Phone
                  </dt>
                  <dd className="font-instrument text-xl">
                    <a href="tel:+15169685204" className="underline underline-offset-4 decoration-[hsl(var(--velto-beige))]/30 hover:decoration-[hsl(var(--velto-beige))]">
                      (516) 968-5204
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="font-sweet-bold font-bold uppercase tracking-[0.17em] text-[10px] text-[hsl(var(--velto-copper-light))] mb-1.5">
                    Location
                  </dt>
                  <dd className="font-instrument text-xl leading-snug">
                    160 Van Brunt St
                    <br />
                    Red Hook, Brooklyn
                  </dd>
                </div>
                <div>
                  <dt className="font-sweet-bold font-bold uppercase tracking-[0.17em] text-[10px] text-[hsl(var(--velto-copper-light))] mb-1.5">
                    Instagram
                  </dt>
                  <dd className="font-instrument text-xl">
                    <a
                      href="https://instagram.com/veltopickleball"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-4 decoration-[hsl(var(--velto-beige))]/30 hover:decoration-[hsl(var(--velto-beige))]"
                    >
                      @veltopickleball
                    </a>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        {/* ---------------- HERO ----------------
            Second block on the page, not the first — the inquiry form sits
            above it. Both grounds are forest, so the hairline is what keeps
            the seam between them readable. */}
        <section className="relative overflow-hidden border-t border-[hsl(var(--velto-beige))]/15 bg-[hsl(var(--velto-forest))]">
          <img
            src="/events/hero.webp"
            srcSet={eventSrcSet("/events/hero.webp")}
            sizes="100vw"
            alt="The lounge and locker area at Velto Pickleball Club in Red Hook, Brooklyn"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover opacity-[0.34]"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(100deg, hsl(var(--velto-forest)) 0%, hsl(var(--velto-forest) / 0.72) 48%, hsl(var(--velto-forest) / 0.42) 100%)",
            }}
          />
          {/* Top padding runs a little over twice the bottom, so the copy sits
              low-left in the frame the way the deck stages it, not centred. */}
          <div className="container relative mx-auto px-4 md:px-6 pt-28 pb-14 md:pt-44 md:pb-20 text-[hsl(var(--velto-beige))]">
            {/* 26ch is the narrowest width that still lands the deck's three
                lines — "...club," / "...play hard" / "and celebrate well." The
                first line measures 25.4ch, so anything under that spills to
                four. Cormorant Garamond's `ch` is its digit advance (0.477em),
                appreciably wider than its average letter, which is why the
                figure reads high next to the 30-character line it holds. */}
            <h2 className="font-instrument text-4xl md:text-5xl lg:text-6xl leading-[1.03] max-w-[26ch]">
              NYC's premier pickleball club, built for those that play hard and celebrate well.
            </h2>
            <p className="font-sweet mt-5 max-w-[48ch] text-[hsl(var(--velto-beige))]/80 leading-relaxed">
              14,000 square feet in Red Hook, Brooklyn. Four indoor courts, six lounge zones, full hot
              buffet catering and a team that runs the whole thing for you.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button type="button" onClick={scrollToForm} className={CTA}>
                Plan your event
              </button>
              <a
                href="#our-space"
                className="inline-flex items-center justify-center border border-[hsl(var(--velto-beige))]/40 hover:bg-[hsl(var(--velto-beige))]/10 text-[hsl(var(--velto-beige))] font-sweet-bold font-bold uppercase tracking-wider text-xs px-7 py-3.5 transition-colors"
              >
                See the space
              </a>
            </div>
            <p className="mt-10 pt-5 border-t border-[hsl(var(--velto-beige))]/15 flex flex-wrap gap-x-8 gap-y-2 font-sweet-bold font-bold uppercase tracking-[0.15em] text-[10px] text-[hsl(var(--velto-beige))]/60">
              <span>Up to 150 guests</span>
              <span>10 min from FiDi</span>
              <span>160 Van Brunt St</span>
            </p>
          </div>
        </section>

        {/* ---------------- OUR SPACE ---------------- */}
        <section id="our-space" className="py-16 bg-[hsl(var(--velto-beige))]">
          <div className="container mx-auto px-4 md:px-6 grid gap-10 lg:grid-cols-2 lg:gap-14 items-center">
            <div>
              <p className={`${EYEBROW} text-[hsl(var(--velto-copper))]`}>Our Space</p>
              <h2 className={H2}>One roof, four courts, and room for the whole company.</h2>
              <dl className="mt-7 border-t border-[hsl(var(--velto-forest))]/15">
                {SPACE_STATS.map(([figure, detail]) => (
                  <div
                    key={figure}
                    className="grid grid-cols-[88px_1fr] sm:grid-cols-[118px_1fr] gap-4 sm:gap-5 items-baseline py-4 border-b border-[hsl(var(--velto-forest))]/15"
                  >
                    <dt className="font-instrument text-[27px] sm:text-[33px] leading-none tabular-nums">{figure}</dt>
                    <dd className="font-sweet text-[13px] sm:text-sm leading-snug text-[hsl(var(--velto-forest))]/80">{detail}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div className="group col-span-2 aspect-[16/10] overflow-hidden bg-[hsl(var(--velto-forest))]/10">
                <EventImage name="courts-play" alt="Members playing doubles on the indoor courts at Velto" sizes="(min-width:1024px) 45vw, 92vw" />
              </div>
              <div className="group aspect-square overflow-hidden bg-[hsl(var(--velto-forest))]/10">
                <EventImage name="lounge-dog" alt="A dog on the lounge sofa overlooking the courts" sizes="(min-width:1024px) 22vw, 46vw" />
              </div>
              <div className="group aspect-square overflow-hidden bg-[hsl(var(--velto-forest))]/10">
                <EventImage name="lounge-table" alt="Lounge seating and the long communal table at Velto" sizes="(min-width:1024px) 22vw, 46vw" />
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- OUR ADVANTAGE ---------------- */}
        <section className="py-16 bg-[hsl(var(--velto-forest))] text-[hsl(var(--velto-beige))]">
          <div className="container mx-auto px-4 md:px-6 grid gap-10 lg:grid-cols-2 lg:gap-14 items-center">
            <div className="grid grid-cols-2 gap-2.5 lg:order-first">
              <div className="group col-span-2 aspect-[16/10] overflow-hidden bg-[hsl(var(--velto-beige))]/5">
                <EventImage name="group-courts" alt="A large corporate group gathered across all four courts" sizes="(min-width:1024px) 45vw, 92vw" />
              </div>
              <div className="group aspect-square overflow-hidden bg-[hsl(var(--velto-beige))]/5">
                <EventImage name="bar-guests" alt="Guests at the branded matcha activation cart" sizes="(min-width:1024px) 22vw, 46vw" />
              </div>
              <div className="group aspect-square overflow-hidden bg-[hsl(var(--velto-beige))]/5">
                <EventImage name="court-screens" alt="Live match scoring on the overhead screens" sizes="(min-width:1024px) 22vw, 46vw" />
              </div>
            </div>
            <div>
              <p className={`${EYEBROW} text-[hsl(var(--velto-copper-light))]`}>Our Advantage</p>
              <h2 className={H2}>A curated experience that will be sure to have everyone talking.</h2>
              <ul className="mt-7 border-t border-[hsl(var(--velto-beige))]/15">
                {ADVANTAGES.map(([title, copy]) => (
                  <li key={title} className="py-4 border-b border-[hsl(var(--velto-beige))]/15">
                    <h3 className="font-sweet-bold font-bold text-sm mb-1">{title}</h3>
                    <p className="font-sweet text-[13px] leading-snug text-[hsl(var(--velto-beige))]/75">{copy}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ---------------- AMENITIES ---------------- */}
        <section className="py-16 bg-[hsl(var(--velto-beige))]">
          <div className="container mx-auto px-4 md:px-6 grid gap-10 lg:grid-cols-2 lg:gap-14 items-center">
            <div>
              <p className={`${EYEBROW} text-[hsl(var(--velto-copper))]`}>Our Amenities</p>
              <h2 className={H2}>Everything you need, already on site.</h2>
              <ul className="mt-6 grid sm:grid-cols-2 gap-x-10">
                {AMENITIES.map(([title, copy]) => (
                  <li key={title} className="py-4 border-b border-[hsl(var(--velto-forest))]/15">
                    <h3 className="font-sweet-bold font-bold text-[13px] mb-1">{title}</h3>
                    <p className="font-sweet text-[13px] leading-snug text-[hsl(var(--velto-forest))]/72">{copy}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div className="group aspect-square overflow-hidden bg-[hsl(var(--velto-forest))]/10">
                <EventImage name="photo-booth" alt="Guests in the on-site photo booth at Velto" sizes="(min-width:1024px) 22vw, 46vw" />
              </div>
              <div className="group aspect-square overflow-hidden bg-[hsl(var(--velto-forest))]/10">
                <EventImage name="phone-booths" alt="Soundproofed private phone booths beside the courts" sizes="(min-width:1024px) 22vw, 46vw" />
              </div>
              <div className="group col-span-2 aspect-[16/10] overflow-hidden bg-[hsl(var(--velto-forest))]/10">
                <EventImage name="lounge-crest" alt="Lounge armchairs beneath the Velto crest" sizes="(min-width:1024px) 45vw, 92vw" />
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- ON-COURT OFFERINGS ----------------
            Four cards on desktop; a snap-scroll rail under 768px, so the row
            costs one screen of height on a phone instead of four. */}
        <section className="py-16 bg-[hsl(var(--velto-forest))] text-[hsl(var(--velto-beige))]">
          <div className="container mx-auto px-4 md:px-6">
            <p className={`${EYEBROW} text-[hsl(var(--velto-copper-light))]`}>On-Court Offerings</p>
            <h2 className={H2}>If you can dream it, we can do it.</h2>
            <ul className="mt-8 flex md:grid md:grid-cols-4 gap-3 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 pb-3 md:pb-0">
              {ON_COURT.map((item) => (
                <li
                  key={item.title}
                  className="shrink-0 basis-[76%] sm:basis-[46%] md:basis-auto snap-start border border-[hsl(var(--velto-beige))]/15 bg-[hsl(var(--velto-beige))]/5"
                >
                  <div className="group aspect-[4/3] overflow-hidden">
                    <EventImage name={item.img} alt={item.alt} sizes="(min-width:768px) 23vw, 76vw" />
                  </div>
                  <div className="p-4 pb-5">
                    <h3 className="font-instrument text-xl mb-1.5">{item.title}</h3>
                    <p className="font-sweet text-[13px] leading-snug text-[hsl(var(--velto-beige))]/75">{item.copy}</p>
                  </div>
                </li>
              ))}
            </ul>
            {/* Two lines, one sentence each. The first sentence measures
                53.2ch in Cormorant, so the box has to clear that or it wraps
                mid-thought; the block span then guarantees the second starts
                fresh instead of relying on where the line happens to break.
                Extra width past 53ch costs nothing here — the text is centred,
                so each sentence centres itself inside whatever box it gets. */}
            <p className="mt-10 pt-8 border-t border-[hsl(var(--velto-beige))]/15 font-instrument text-2xl md:text-3xl leading-snug text-center max-w-[56ch] mx-auto">
              We can build around your needs or plan everything from A to Z.{" "}
              <span className="block">Just show up and we handle the rest.</span>
            </p>
          </div>
        </section>

        {/* ---------------- OFF-COURT OFFERINGS ---------------- */}
        <section className="py-16 bg-[hsl(var(--velto-beige))]">
          <div className="container mx-auto px-4 md:px-6 grid gap-10 lg:grid-cols-2 lg:gap-14 items-center">
            <div>
              <p className={`${EYEBROW} text-[hsl(var(--velto-copper))]`}>Off-Court Offerings</p>
              <h2 className={H2}>Catering and decor that elevates your event.</h2>
              <ol className="mt-7 border-t border-[hsl(var(--velto-forest))]/15">
                {OFF_COURT.map(([name, detail], i) => (
                  <li
                    key={name}
                    className="grid grid-cols-[36px_1fr] gap-4 items-baseline py-4 border-b border-[hsl(var(--velto-forest))]/15"
                  >
                    <span className="font-instrument text-[22px] leading-none text-[hsl(var(--velto-forest))]/45 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-sweet text-[13px] sm:text-sm leading-snug text-[hsl(var(--velto-forest))]/85">
                      <strong className="font-sweet-bold font-bold">{name}</strong> — {detail}
                    </span>
                  </li>
                ))}
              </ol>
              <p className="mt-5 font-sweet text-[13px] leading-relaxed text-[hsl(var(--velto-forest))]/70">
                All beverages are included with every package: soft drinks, lemonade, iced tea,
                fruit-infused water, coffee and tea.
              </p>
            </div>
            <div className="grid grid-cols-2 grid-rows-2 gap-2.5 aspect-[1/0.92]">
              <div className="group row-span-2 overflow-hidden bg-[hsl(var(--velto-forest))]/10">
                <EventImage name="matcha-cart" alt="Matcha and strawberry drinks on the activation cart" sizes="(min-width:1024px) 22vw, 46vw" />
              </div>
              <div className="group overflow-hidden bg-[hsl(var(--velto-forest))]/10">
                <EventImage name="bar" alt="Guests at the Velto bar during an event" sizes="(min-width:1024px) 22vw, 46vw" />
              </div>
              <div className="group overflow-hidden bg-[hsl(var(--velto-forest))]/10">
                <EventImage name="lounge-court" alt="Lounge seating beside the court" sizes="(min-width:1024px) 22vw, 46vw" />
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- SOCIAL BAND ----------------
            Full-width photography strip: a pause between the offerings and
            the packages, not a content section, so it carries no heading.
            A "Previous Clients" title and a ten-mark client logo wall stood
            here until Sep 2026; both were dropped at the owner's request and
            the photos kept. The logo files remain in public/events/. */}
        <section className="py-16 bg-[hsl(var(--velto-forest))] text-[hsl(var(--velto-beige))]">
          <div className="container mx-auto px-4 md:px-6">
            <ul className="grid grid-cols-2 min-[700px]:grid-cols-4 gap-2.5 md:gap-3">
              {SOCIAL_SHOTS.map((shot) => (
                <li key={shot.img} className="group aspect-[4/5] overflow-hidden bg-[hsl(var(--velto-beige))]/5">
                  <EventImage name={shot.img} alt={shot.alt} sizes="(min-width:700px) 23vw, 46vw" />
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------------- PACKAGES ----------------
            No prices on the page by owner decision — every tier quotes on
            enquiry, so the spec rows do the qualifying instead. */}
        <section id="packages" className="py-16 bg-[hsl(var(--velto-beige))]">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-end justify-between gap-5">
              <div>
                <p className={`${EYEBROW} text-[hsl(var(--velto-copper))]`}>Packages</p>
                <h2 className={H2}>Find the right fit.</h2>
              </div>
              <p className="font-sweet text-[13px] text-[hsl(var(--velto-forest))]/70 max-w-[32ch] md:text-right">
                Every package includes paddles, balls, on-site coaching and lounge access.
              </p>
            </div>

            <div className="mt-7 overflow-x-auto">
              <table className="w-full min-w-[660px] border-collapse font-sweet text-[13.5px]">
                <caption className="sr-only">
                  Velto private event packages compared by guest count, duration, courts, coaches,
                  lounge access, arcade technology and food and beverage. Pricing is quoted on
                  enquiry.
                </caption>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="text-left align-bottom py-3 px-4 font-sweet-bold font-bold uppercase tracking-[0.16em] text-[10.5px] text-[hsl(var(--velto-copper))] border-b border-[hsl(var(--velto-forest))]/40"
                    >
                      Package
                    </th>
                    {PACKAGES.map((p) => (
                      <th
                        key={p.name}
                        scope="col"
                        className={`text-left align-bottom py-3 px-4 font-instrument text-[25px] font-semibold border-b border-[hsl(var(--velto-forest))]/40 ${p.highlight ? "bg-[hsl(var(--velto-copper))]/10" : ""}`}
                      >
                        {p.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PACKAGE_ROWS.map(([label, key]) => (
                    <tr key={label}>
                      <th
                        scope="row"
                        className="text-left py-3 px-4 font-normal text-[12.5px] text-[hsl(var(--velto-forest))]/70 whitespace-nowrap border-b border-[hsl(var(--velto-forest))]/12"
                      >
                        {label}
                      </th>
                      {PACKAGES.map((p) => (
                        <td
                          key={p.name}
                          className={`py-3 px-4 border-b border-[hsl(var(--velto-forest))]/12 ${p.highlight ? "bg-[hsl(var(--velto-copper))]/10" : ""}`}
                        >
                          {p[key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <th
                      scope="row"
                      className="text-left py-3 px-4 font-sweet-bold font-bold text-[12.5px] whitespace-nowrap border-b border-[hsl(var(--velto-forest))]/12"
                    >
                      Pricing
                    </th>
                    {PACKAGES.map((p) => (
                      <td
                        key={p.name}
                        className={`py-3 px-4 font-instrument text-[19px] text-[hsl(var(--velto-copper))] border-b border-[hsl(var(--velto-forest))]/12 ${p.highlight ? "bg-[hsl(var(--velto-copper))]/10" : ""}`}
                      >
                        Contact for pricing
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="md:hidden mt-2.5 font-sweet-bold font-bold uppercase tracking-[0.13em] text-[10.5px] text-[hsl(var(--velto-copper))]">
              Scroll the table sideways to compare →
            </p>

            <p className="mt-4 font-sweet text-[12.5px] leading-relaxed text-[hsl(var(--velto-forest))]/70 max-w-[70ch]">
              A Soirée package is also available for up to 20 guests on one court for two hours. Extra
              hours can be added to any package. We are happy to work with your budget — ask about
              transportation add-ons when you enquire.
            </p>
            <button type="button" onClick={scrollToForm} className={`${CTA} mt-6`}>
              Request pricing
            </button>
          </div>
        </section>

        {/* ---------------- ADD-ONS ---------------- */}
        <section className="py-16 bg-[hsl(var(--velto-forest))] text-[hsl(var(--velto-beige))]">
          <div className="container mx-auto px-4 md:px-6">
            <p className={`${EYEBROW} text-[hsl(var(--velto-copper-light))]`}>Add-Ons</p>
            <h2 className={H2}>Make it memorable.</h2>
            <p className="font-sweet mt-4 max-w-[56ch] text-[hsl(var(--velto-beige))]/78 leading-relaxed">
              Layer any of these onto your package. Custom colour palettes and branded elements
              available.
            </p>

            <div className="mt-8 grid gap-9 lg:grid-cols-2 lg:gap-12">
              <div>
                <div className="group aspect-[1.42] max-w-[340px] overflow-hidden mb-5 bg-[hsl(var(--velto-beige))]/5">
                  <EventImage name="decor" alt="Balloon garland and branded step-and-repeat backdrop" sizes="340px" />
                </div>
                <p className="font-sweet-bold font-bold uppercase tracking-[0.18em] text-[10.5px] text-[hsl(var(--velto-beige))]/60 pb-3 border-b border-[hsl(var(--velto-beige))]/15">
                  Decor packages
                </p>
                <ul>
                  {DECOR_PACKAGES.map((d) => (
                    <li
                      key={d.name}
                      className="flex items-baseline justify-between gap-5 py-3.5 border-b border-[hsl(var(--velto-beige))]/15"
                    >
                      <span>
                        <span className="font-instrument text-xl">{d.name}</span>
                        {d.popular && (
                          <span className="ml-2 align-middle inline-block bg-[hsl(var(--velto-copper))] text-[hsl(var(--velto-beige))] font-sweet-bold font-bold uppercase tracking-[0.13em] text-[9px] px-1.5 py-0.5">
                            Most popular
                          </span>
                        )}
                        <span className="block font-sweet text-[12.5px] mt-0.5 text-[hsl(var(--velto-beige))]/65">
                          {d.detail}
                        </span>
                      </span>
                      <span className="shrink-0 font-sweet-bold font-bold uppercase tracking-[0.13em] text-[10.5px] text-[hsl(var(--velto-copper-light))]">
                        Enquire
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="group aspect-[1.42] max-w-[340px] overflow-hidden mb-5 bg-[hsl(var(--velto-beige))]/5">
                  <EventImage name="carts" alt="The Velto premium food and beverage carts" sizes="340px" />
                </div>
                <p className="font-sweet-bold font-bold uppercase tracking-[0.18em] text-[10.5px] text-[hsl(var(--velto-beige))]/60 pb-3 border-b border-[hsl(var(--velto-beige))]/15">
                  Premium F&amp;B carts
                </p>
                <ul>
                  {FB_CARTS.map((c) => (
                    <li
                      key={c.name}
                      className="flex items-baseline justify-between gap-5 py-3.5 border-b border-[hsl(var(--velto-beige))]/15 last:border-b-0"
                    >
                      <span>
                        <span className="font-instrument text-xl">{c.name}</span>
                        <span className="block font-sweet text-[12.5px] mt-0.5 text-[hsl(var(--velto-beige))]/65">
                          {c.detail}
                        </span>
                      </span>
                      <span className="shrink-0 font-sweet-bold font-bold uppercase tracking-[0.13em] text-[10.5px] text-[hsl(var(--velto-copper-light))]">
                        Enquire
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Sticky mobile CTA — same pattern as the SEO landing pages. */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex gap-2.5 px-4 py-2.5 bg-[hsl(var(--velto-forest))]/96 backdrop-blur-sm border-t border-[hsl(var(--velto-beige))]/15">
          <a
            href="#packages"
            className="flex-1 inline-flex items-center justify-center border border-[hsl(var(--velto-beige))]/40 text-[hsl(var(--velto-beige))] font-sweet-bold font-bold uppercase tracking-wider text-[11px] py-3"
          >
            Packages
          </a>
          <button type="button" onClick={scrollToForm} className={`${CTA} flex-1 px-4`}>
            Plan your event
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Events;
